import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../i18n';
import { BOT_APP_URL, SUPPORT_URL } from '../links';
import logo from '../assets/figma/logo-horizontal.svg';
import arrowDown from '../assets/figma/icons/arrow-down.svg';
import arrowUp from '../assets/figma/icons/arrow-up.svg';

const LINKS = [
  { key: 'menu' as const, href: '#features' },
  { key: 'channels' as const, href: '#coverage' },
  { key: 'contact' as const, href: SUPPORT_URL, external: true },
];

/** Figma 59:9908 — the floating nav bar. On the design's canvas the links sit
 *  dead centre of the bar independently of the logo and the actions on either
 *  side, so they are positioned rather than laid out between them.
 *
 *  Narrow screens can't hold that row, so the links move into a panel behind a
 *  toggle and the bar keeps only the logo, the language and the call to action. */
export function Header() {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  return (
    <header className="sticky top-0 z-50 flex flex-col items-center justify-center p-[10px] lg:p-[40px]">
      <div className="relative flex w-full items-center justify-between rounded-[999px] bg-white/[0.76] p-[6px] pl-[14px] shadow-[0px_0px_60px_0px_rgba(22,22,23,0.04)] backdrop-blur-[20px] lg:p-[12px] lg:pl-[12px]">
        {/* Width only, and inline so it escapes the px-to-rem step: given both
            dimensions the SVG is squeezed into a box, and on the page's
            fractional scale it lands between pixel rows and looks soft. */}
        <img
          src={logo}
          alt="AsiaWallet"
          className="shrink-0"
          style={{ width: 112, height: 'auto' }}
        />

        {/* centre links — absolutely placed, per the design */}
        <nav className="absolute left-[calc(50%-0.5px)] top-[13px] hidden -translate-x-1/2 items-center gap-[4px] lg:flex">
          {LINKS.map(({ key, href, external }) => (
            <a
              key={key}
              href={href}
              {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="flex items-center justify-center rounded-[999px] p-[10px] text-[15px] font-medium text-ink"
            >
              {t(`nav.${key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center justify-end gap-[4px] lg:items-end lg:gap-[8px]">
          {/* language */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setLangOpen((o) => !o)}
              className="flex items-center justify-center gap-[4px] rounded-[999px] p-[6px] text-[14px] font-medium text-ink lg:p-[10px] lg:text-[15px]"
            >
              {current.label}
              <img src={arrowDown} alt="" className="size-[16px]" />
            </button>
            {langOpen && (
              <ul className="absolute right-0 top-[calc(100%+4px)] z-10 overflow-hidden rounded-[16px] bg-white shadow-[0px_0px_60px_0px_rgba(22,22,23,0.12)]">
                {LANGUAGES.map((l) => (
                  <li key={l.code}>
                    <button
                      type="button"
                      onClick={() => {
                        void i18n.changeLanguage(l.code);
                        setLangOpen(false);
                      }}
                      className="w-full px-[16px] py-[10px] text-left text-[15px] font-medium text-ink hover:bg-black/[0.04]"
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <a
            href={BOT_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden shrink-0 items-center justify-center gap-[3px] rounded-[999px] bg-primary px-[16px] py-[10px] text-[15px] font-medium text-white lg:flex"
          >
            {t('nav.cta')}
            <img src={arrowUp} alt="" className="size-[16px]" />
          </a>

          {/* menu toggle — narrow screens only */}
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={t('nav.menu')}
            aria-expanded={menuOpen}
            className="flex size-[32px] shrink-0 flex-col items-center justify-center gap-[4px] rounded-[999px] lg:hidden"
          >
            <span
              className={`block h-[2px] w-[18px] rounded-full bg-ink transition-transform ${
                menuOpen ? 'translate-y-[6px] rotate-45' : ''
              }`}
            />
            <span
              className={`block h-[2px] w-[18px] rounded-full bg-ink transition-opacity ${
                menuOpen ? 'opacity-0' : ''
              }`}
            />
            <span
              className={`block h-[2px] w-[18px] rounded-full bg-ink transition-transform ${
                menuOpen ? '-translate-y-[6px] -rotate-45' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className="mt-[8px] flex w-full flex-col overflow-hidden rounded-[24px] bg-white/[0.92] shadow-[0px_0px_60px_0px_rgba(22,22,23,0.08)] backdrop-blur-[20px] lg:hidden">
          {LINKS.map(({ key, href, external }) => (
            <a
              key={key}
              href={href}
              onClick={() => setMenuOpen(false)}
              {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="px-[20px] py-[14px] text-[16px] font-medium text-ink active:bg-black/[0.04]"
            >
              {t(`nav.${key}`)}
            </a>
          ))}
          <a
            href={BOT_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuOpen(false)}
            className="m-[12px] mt-[4px] flex items-center justify-center gap-[3px] rounded-[999px] bg-primary px-[16px] py-[12px] text-[16px] font-medium text-white"
          >
            {t('nav.cta')}
            <img src={arrowUp} alt="" className="size-[16px]" />
          </a>
        </nav>
      )}
    </header>
  );
}
